// ui/a2hs.ts — "Add to Home Screen" nudge for iOS Safari on the WEB.
//
// iOS gives no install-prompt API, so this is pure instructional: when the game is opened in
// mobile Safari (NOT already launched from the Home Screen, NOT an in-app WebView, NOT Chrome/
// Firefox/etc on iOS which can't A2HS), show a one-time dismissible modal teaching the
// Share → Add to Home Screen gesture. Fullscreen home-screen play kills the Safari chrome and
// gives the game the whole viewport, so it's genuinely a better experience — hence the nudge.
//
// Self-contained: injects its own <style> + DOM, no edits to index.html/style.css needed.
// Deliberately NAGGY — it shows on EVERY web visit (no dismiss latch) to keep motivating players
// to install. The only thing that stops it is actually adding to the Home Screen: once launched
// from there, isStandalone() is true and this never fires. Dismiss is per-view only (✕ / "Got it",
// no backdrop dismiss); a MutationObserver keeps it mounted against boot-time overlay sweeps.

// Standalone = launched from the Home Screen (iOS `navigator.standalone`, or the CSS display-mode).
function isStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return true;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

// iOS / iPadOS. iPadOS 13+ masquerades as "Macintosh" but reports touch points, so catch that too.
function isIOS(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

// Safari proper — every iOS browser is WebKit under the hood, but only Safari exposes the
// Share → Add to Home Screen flow, so exclude the wrapped browsers by their UA tokens.
function isSafari(): boolean {
  const ua = navigator.userAgent;
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA|DuckDuckGo|Brave|Mercury/i.test(ua)) return false;
  return /Safari/.test(ua);
}

// The iOS Share glyph (square with an up-arrow), inline so it renders identically to what Ara sees.
const SHARE_GLYPH = `
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none"
       stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
    <path d="M12 3v12" />
    <path d="M8 7l4-4 4 4" />
    <path d="M6 11H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-1" />
  </svg>`;

// The "+" tile that sits next to "Add to Home Screen" in the iOS share sheet.
const PLUS_GLYPH = `
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none"
       stroke="currentColor" stroke-width="1.9" stroke-linecap="round">
    <path d="M12 6v12M6 12h12" />
  </svg>`;

function injectStyle(): void {
  if (document.getElementById('a2hs-style')) return;
  const s = document.createElement('style');
  s.id = 'a2hs-style';
  s.textContent = `
  .a2hs-back {
    position: fixed; inset: 0; z-index: 999;
    display: flex; align-items: flex-end; justify-content: center;
    background: rgba(3, 10, 9, 0.62);
    -webkit-backdrop-filter: blur(6px); backdrop-filter: blur(6px);
    padding: 0 12px calc(16px + var(--sab)) 12px;
    opacity: 0; transition: opacity .22s ease;
  }
  .a2hs-back.is-in { opacity: 1; }
  .a2hs-sheet {
    width: 100%; max-width: 420px;
    background: linear-gradient(180deg, var(--card-2), var(--card));
    border: 1px solid var(--line); border-radius: 20px;
    box-shadow: 0 -8px 40px rgba(0,0,0,.5);
    padding: 20px 20px 18px; color: var(--ink);
    transform: translateY(14px); transition: transform .24s cubic-bezier(.2,.8,.2,1);
  }
  .a2hs-back.is-in .a2hs-sheet { transform: translateY(0); }
  .a2hs-head { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
  .a2hs-eyebrow { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--teal); font-weight: 700; }
  .a2hs-x {
    margin-left: auto; width: 30px; height: 30px; border-radius: 50%;
    border: 1px solid var(--line); background: transparent; color: var(--dim);
    font-size: 17px; line-height: 1; cursor: pointer;
  }
  .a2hs-title { font-size: 20px; font-weight: 800; margin: 2px 0 6px; }
  .a2hs-body { font-size: 14px; line-height: 1.5; color: var(--dim); margin: 0 0 16px; }
  .a2hs-steps { list-style: none; margin: 0 0 18px; padding: 0; display: grid; gap: 12px; }
  .a2hs-step { display: flex; align-items: center; gap: 12px; font-size: 14.5px; line-height: 1.35; }
  .a2hs-num {
    flex: 0 0 auto; width: 24px; height: 24px; border-radius: 50%;
    display: grid; place-items: center; font-size: 12px; font-weight: 800;
    background: var(--teal-deep); color: var(--teal); border: 1px solid rgba(79,209,176,.35);
  }
  .a2hs-chip {
    display: inline-flex; align-items: center; gap: 5px; vertical-align: middle;
    padding: 2px 8px; margin: 0 2px; border-radius: 8px;
    background: rgba(79,209,176,.12); color: var(--teal); font-weight: 700;
    border: 1px solid rgba(79,209,176,.28);
  }
  .a2hs-chip svg { display: block; }
  .a2hs-ok {
    width: 100%; padding: 13px; border: none; border-radius: 14px; cursor: pointer;
    font-size: 15px; font-weight: 800; color: #04140f;
    background: linear-gradient(90deg, var(--teal), #8cf6d4);
  }
  `;
  document.head.appendChild(s);
}

// Show the modal once and latch. Returns nothing; safe to call unconditionally — it self-gates.
export function maybeShowA2HS(): void {
  if (isStandalone() || !isIOS() || !isSafari()) return;

  injectStyle();

  const back = document.createElement('div');
  back.className = 'a2hs-back';
  back.setAttribute('role', 'dialog');
  back.setAttribute('aria-modal', 'true');
  back.setAttribute('aria-label', 'Add Microbe RTS to your Home Screen');
  back.innerHTML = `
    <div class="a2hs-sheet">
      <div class="a2hs-head">
        <span class="a2hs-eyebrow">Play it better</span>
        <button type="button" class="a2hs-x" aria-label="Dismiss">✕</button>
      </div>
      <div class="a2hs-title">Add to Home Screen</div>
      <p class="a2hs-body">
        Safari's toolbars steal the deep. Add Microbe&nbsp;RTS to your Home Screen and it opens
        full-screen, like a real app. No address bar, no clutter.
      </p>
      <ol class="a2hs-steps">
        <li class="a2hs-step">
          <span class="a2hs-num">1</span>
          <span>Tap the <span class="a2hs-chip">${SHARE_GLYPH} Share</span> icon in Safari's bar.</span>
        </li>
        <li class="a2hs-step">
          <span class="a2hs-num">2</span>
          <span>Scroll and choose <span class="a2hs-chip">${PLUS_GLYPH} Add to Home Screen</span>.</span>
        </li>
        <li class="a2hs-step">
          <span class="a2hs-num">3</span>
          <span>Tap <b>Add</b>, then launch it from your Home Screen.</span>
        </li>
      </ol>
      <button type="button" class="a2hs-ok">Got it</button>
    </div>`;

  // The modal kept flashing away even with a guarded backdrop, so something OTHER than a tap is
  // pulling the node out of the DOM at boot (a re-render / overlay sweep / stray reload settle).
  // Rather than hunt every possible remover, the modal is made UN-REMOVABLE except by its own
  // close CTAs: a MutationObserver re-mounts it the instant anything detaches it. Because the
  // detached node keeps its `is-in` class, the re-mount is seamless (no re-fade, no flash).
  // Per Ara: there is NO backdrop-to-dismiss — it stays until the ✕ or "Got it" is tapped.
  let dismissed = false;

  function mount(): void {
    if (dismissed || document.body.contains(back)) return;
    document.body.appendChild(back);
    requestAnimationFrame(() => back.classList.add('is-in'));
  }

  // Watch the body: if `back` is ever removed while not dismissed, put it straight back.
  const guard = new MutationObserver(() => mount());
  guard.observe(document.body, { childList: true });

  // Per-view dismiss only — no persistence. Closing lets them play now; it returns next reload.
  function close(): void {
    dismissed = true;
    guard.disconnect();
    back.classList.remove('is-in');
    setTimeout(() => back.remove(), 240);
  }

  back.querySelector<HTMLButtonElement>('.a2hs-x')?.addEventListener('click', close);
  back.querySelector<HTMLButtonElement>('.a2hs-ok')?.addEventListener('click', close);

  mount();
  // Belt-and-suspenders: the observer only fires on DOM mutations, so also re-assert on a short
  // timer through the boot window, then stop so nothing lingers. Cleared early on dismiss.
  const keepAlive: ReturnType<typeof setInterval> = setInterval(mount, 250);
  setTimeout(() => clearInterval(keepAlive), 8000);
}
